import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  Tag,
  useToaster,
  Pagination,
  IconButton,
  Modal,
  Message,
  Whisper,
  Tooltip
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import {
  MdFilterList,
  MdOutlineDeleteOutline,
  MdInfoOutline,
  MdNotifications
} from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import NotificationSettingsDrawer from '../NotificationSettingsDrawer';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWFHRequests } from '@/services/wfh.service';
import { deleteRequest } from '@/services/request.service';
import { getAdminUsers } from '@/services/user.service';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import DetailsDrawer from './DetailsDrawer';
import { IoMdNotificationsOutline } from 'react-icons/io';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { t } = useTranslation();
  const { data, loading, pagination } = useSelector(state => state.wfh);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showNotificationDrawer, setShowNotificationDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    userId: '',
    status: '',
    validatorId: ''
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState({ open: false, data: null });
  const [loadingExport, setLoadingExport] = useState(false);

  // Filter users to exclude those with forgotten: true
  const filteredUsers = users.filter(user => user.forgotten !== true);

  useEffect(() => {
    dispatch(getAdminUsers());
  }, [dispatch]);

  const applyFilters = (dataToFilter, searchTerm, currentFilters) => {
    let filtered = [...dataToFilter];

    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter(item => {
        return item.operations.some(operation => {
          const operationDate = new Date(operation.date);
          operationDate.setHours(0, 0, 0, 0);
          return operationDate >= startDate && operationDate <= endDate;
        });
      });
    }

    if (searchTerm.trim()) {
      const searchStr = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const fullName = `${item?.idSender?.firstname} ${item?.idSender?.lastname}`.toLowerCase();
        const registrationNumber = item.idSender?.registrationNumber?.toLowerCase() || '';
        const status = item._id?.status?.toLowerCase() || '';

        return (
          fullName.includes(searchStr) ||
          registrationNumber.includes(searchStr) ||
          status.includes(searchStr)
        );
      });
    }

    if (currentFilters.userId) {
      filtered = filtered.filter(item => item.idSender?._id === currentFilters.userId);
    }

    if (currentFilters.validatorId) {
      filtered = filtered.filter(item => item.idReciever?._id === currentFilters.validatorId);
    }

    if (currentFilters.status) {
      filtered = filtered.filter(item => item._id.status === currentFilters.status);
    }

    return filtered;
  };

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      dispatch(
        fetchWFHRequests(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd'),
          page,
          limit
        )
      );
    }
  }, [dateRange, page, limit, dispatch]);

  const filteredData = applyFilters(data, searchKeyword, filters);

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      status: '',
      validatorId: ''
    });
    setPage(1);
  };

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'yellow';
      case 'accepted':
        return 'green';
      case 'accept':
        return 'green';
      case 'refused':
        return 'red';
      default:
        return 'grey';
    }
  };

  const getStatus = status => {
    switch (status) {
      case 'pending':
        return t('pending');
      case 'accepted':
        return t('approved');
      case 'refused':
        return t('rejected');
      default:
        return t('unknown');
    }
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = async () => {
    if (!dateRange) {
      toaster.push(
        <Message type="warining" duration={8000}>
          {t('Select period please')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }
    setLoadingExport(true);
    const data = await fetchWFHRequests(
      dateRange[0] ? format(new Date(dateRange[0]), 'yyyy-MM-dd') : null,
      dateRange[1] ? format(new Date(dateRange[1]), 'yyyy-MM-dd') : null,
      null,
      null,
      false
    )();

    const wfhRequestsToExport = data?.data.map(item => {
      const requestedDays = item.operations
        .map(
          operation => `${new Date(operation.date).toLocaleDateString()} - ${operation.timeslot}`
        )
        .join(', ');

      return {
        'Full Name': `${item.idSender?.firstname || ''} ${item.idSender?.lastname || ''}`,
        'Registration Number': item.idSender?.registrationNumber || '',
        'Days Requested': requestedDays,
        Status: item._id?.status || '',
        'Created At': new Date(item._id.createdAt).toLocaleString()
      };
    });

    downloadExcel(
      wfhRequestsToExport,
      `wfh_${format(new Date(dateRange[0]), 'dd-MM-yyyy')}_${format(
        new Date(dateRange[1]),
        'dd-MM-yyyy'
      )}`
    );
    setLoadingExport(false);
  };

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  const handleDelete = rowData => {
    setSelectedRequest(rowData);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const requestId = selectedRequest._id._id || selectedRequest._id;
      await dispatch(deleteRequest(requestId));

      toaster.push(
        <Message type="success" duration={3000}>
          {t('Request deleted successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);

      if (dateRange && dateRange[0]) {
        const startDate = new Date(dateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
        endDate.setHours(23, 59, 59, 999);

        dispatch(
          fetchWFHRequests(
            format(startDate, 'yyyy-MM-dd'),
            format(endDate, 'yyyy-MM-dd'),
            page,
            limit
          )
        );
      }
    } catch (error) {
      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error deleting request:')} {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);
    }
  };

  const handleDetails = rowData => {
    setShowDetailsDrawer({ open: true, data: rowData });
  };

  const handleNotificationSettings = () => {
    setShowNotificationDrawer(true);
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <DateRangePicker
          appearance="default"
          placeholder={t('Select Date Range')}
          style={{ width: 230 }}
          value={dateRange}
          onChange={setDateRange}
          defaultValue={dateRange}
          editable={false}
          ranges={[
            {
              label: t('Last 30 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
            },
            {
              label: t('Last 7 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]
            },
            {
              label: t('Today'),
              value: [new Date(new Date().setDate(new Date().getDate())), new Date()]
            }
          ]}
        />

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button
            appearance="primary"
            onClick={handleExport}
            loading={loadingExport}
            disabled={loadingExport}
          >
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
          <Button appearance="primary" onClick={handleNotificationSettings}>
            <Icon as={IoMdNotificationsOutline} style={{ marginRight: 8 }} />{' '}
            {t('Notification Settings')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 500)}
          data={filteredData}
          loading={loading}
          rowHeight={80}
          wordWrap="break-word"
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
            <Cell>
              {rowData =>
                `${rowData.idSender?.firstname || ''} ${rowData.idSender?.lastname || ''}`
              }
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell>{rowData => rowData.idSender?.registrationNumber}</Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Days Requested')}</HeaderCell>
            <Cell style={{ whiteSpace: 'pre-line' }}>
              {rowData => {
                return rowData.operations
                  .map(
                    (operation, index) =>
                      `${new Date(operation.date).toLocaleDateString()} - ${operation.timeslot}${
                        index < rowData.operations.length - 1 ? '\n' : ''
                      }`
                  )
                  .join('');
              }}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Status')}</HeaderCell>
            <Cell>
              {rowData => (
                <Tag color={getStatusColor(rowData._id?.status)}>
                  {getStatus(rowData._id?.status)}
                </Tag>
              )}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Created At')}</HeaderCell>
            <Cell>{rowData => new Date(rowData._id.createdAt).toLocaleString()}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Validator')}</HeaderCell>
            <Cell>
              {rowData => {
                const receiverId = rowData.idReciever ? rowData.idReciever._id : null;
                const validator = receiverId
                  ? filteredUsers.find(user => user._id === receiverId)
                  : null;
                return validator ? `${validator.firstname} ${validator.lastname}` : t('N/A');
              }}
            </Cell>
          </Column>

          <Column width={100} flexGrow={0.5} fixed="right">
            <HeaderCell>{t('Actions')}</HeaderCell>
            <Cell style={{ padding: '6px' }}>
              {rowData => (
                <Stack spacing={6}>
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip>{t('View Details')}</Tooltip>}
                  >
                    <IconButton
                      size="xs"
                      icon={<Icon as={MdInfoOutline} />}
                      onClick={() => handleDetails(rowData)}
                      id={`details-${rowData._id._id}`}
                    />
                  </Whisper>
                  <IconButton
                    size="xs"
                    icon={<MdOutlineDeleteOutline color="red" />}
                    onClick={() => handleDelete(rowData)}
                    id={`delete-${rowData._id._id}`}
                  />
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={pagination?.total || filteredData.length}
            limitOptions={[10, 20, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Home office Request')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            'Are you sure you want to delete this Home office request? This action cannot be undone.'
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={filteredUsers}
        usersLoading={usersLoading}
        toaster={toaster}
      />
      <NotificationSettingsDrawer
        open={showNotificationDrawer}
        onClose={() => setShowNotificationDrawer(false)}
        type="WFH"
      />
      <DetailsDrawer
        open={showDetailsDrawer.open}
        onClose={() => setShowDetailsDrawer({ open: false, data: null })}
        data={showDetailsDrawer.data}
      />
    </>
  );
};

export default DataTable;
